import { endOfMonth, startOfMonth } from 'date-fns';
import { ReactNode } from 'react';
import { Download } from 'react-feather';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useLocale } from '../../Shared/locale';
import { AButton } from '../../Shared/UI/Button';
import { Card } from '../../Shared/UI/Card';
import { DateInput } from '../../Shared/UI/DateInput';
import ErrorMessage from '../../Shared/UI/ErrorMessage';
import { Icon } from '../../Shared/UI/Icon';
import InputDescription from '../../Shared/UI/InputDescription';
import InputGroup from '../../Shared/UI/InputGroup';
import InputLabel from '../../Shared/UI/InputLabel';
import { useRouter } from '../router';
import { SharedProps } from '../types';
import Container from '../UI/Container';
import Header from '../UI/Header';
import HeaderTitle from '../UI/HeaderTitle';
import Layout from '../UI/Layout';

export type ReportsPageProps = SharedProps

export default function ReportsPage() {
  const { t } = useTranslation();

  return (
    <>
      <Header>
        <Container size="md">
          <HeaderTitle>
            {t('backend:reports')}
          </HeaderTitle>
        </Container>
      </Header>

      <Container size="md" className="space-y-6">
        <TransactionsReport />
      </Container>
    </>
  );
}

const TransactionsReport = () => {
  const { t } = useTranslation();
  const { formatDate } = useLocale();
  const { routes } = useRouter();

  const form = useForm({
    defaultValues: {
      start_date: formatDate(startOfMonth(new Date), 'internal_date'),
      end_date: formatDate(endOfMonth(new Date), 'internal_date'),
    },
  });

  const { control, watch, formState: { errors } } = form;

  const startDate = watch('start_date');
  const endDate = watch('end_date');

  return (
    <Card panels header>
      <div>
        <h3>
          {t('backend:transactions_report')}
        </h3>
      </div>
      <div>
        <form className="space-y-6">
          <InputDescription>
            {t('backend:transactions_report_description')}
          </InputDescription>

          <div className="grid grid-cols-[1fr_1fr] gap-6">
            <InputGroup>
              <InputLabel htmlFor="transactions_report_start_date">
                {t('backend:start_date')}
              </InputLabel>
              <Controller
                control={control}
                name="start_date"
                rules={{ max: endDate || undefined }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <DateInput
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    ariaInvalid={!!errors.start_date}
                    optional
                    endDate={endDate}
                    selectsStart
                    id="transactions_report_start_date"
                  />
                )}
              />
              <ErrorMessage
                error={errors.start_date}
                attribute={t('backend:start_date')}
                values={{ max: t('backend:end_date') }}
              />
            </InputGroup>
            <InputGroup>
              <InputLabel htmlFor="transactions_report_end_date">
                {t('backend:end_date')}
              </InputLabel>
              <Controller
                control={control}
                name="end_date"
                rules={{ min: startDate || undefined }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <DateInput
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    aria-invalid={!!errors.end_date}
                    optional
                    startDate={startDate}
                    selectsEnd
                    id="transactions_report_end_date"
                  />
                )}
              />
              <ErrorMessage
                error={errors.end_date}
                attribute={t('backend:end_date')}
                values={{ min: t('backend:start_date') }}
              />
            </InputGroup>
          </div>

          <AButton href={routes.download_transactions_report({ start_date: startDate, end_date: endDate })}>
            <Icon className="mr-1">
              <Download />
            </Icon>
            {t('backend:download')}
          </AButton>
        </form>
      </div>
    </Card>
  );
};

ReportsPage.layout = (page: ReactNode) => <Layout>{page}</Layout>;
